import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/source-brand-locations';
export const getSourceBrandLocationsEndpointIdentifier = endpoint;

export type ISourceBrandLocationData = {
  id: number;
  parentId: number | null;
  sourcePlatform: string;
  sourceExternalId: string;
  sourceUrl: string | null;
  name: string;
  address: string | null;
  coordinates: {
    lat: number;
    lng: number;
  } | null;
  rating: number | null;
  ratingsTotal: number | null;
  reviewsTotal: number | null;
  priceLevel: number | null;
  avgCostPerCouple: number | null;
  avgCostCurrency: string | null;
  businessStatus: string | null;
  phoneNumber: string | null;
  website: string | null;
  isMaster: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  brand: {
    id: number;
    name: string;
    image: string | null;
    countryCode: string;
  };
  geoLocation: {
    id: number;
    name: string;
    slug: string;
  } | null;
};

type TData = {
  pageCount: number;
  total: number;
  data: ISourceBrandLocationData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    name: TParam;
    brandId: TParam;
    sourcePlatform: TParam;
  }
];

type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSourceBrandLocationsApi(options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const { getPageParams } = usePageParams();
  const { name, brandId, sourcePlatform } = getPageParams();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
      };

      if (name) {
        params.name = name;
      }

      if (brandId) {
        params.brandId = brandId;
      }

      if (sourcePlatform) {
        params.sourcePlatform = sourcePlatform;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        name,
        brandId,
        sourcePlatform,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
